import { FilterMatchMode, FilterMetadata, FilterOperator } from 'primeng/api';
import { ColumnFilter, ColumnFilterFormElement, Table } from 'primeng/table';

let isPrimeNgColumnFilterPatched = false;
// keep per-column icon state based on last committed Apply/Clear result, not staged popup values.
const appliedFilterStates = new WeakMap<Table, Map<string, boolean>>();
// Preserve the committed constraints while an Apply-enabled menu is being edited.
const pendingFilterStates = new WeakMap<ColumnFilter, FilterMetadata | FilterMetadata[]>();
const tableColumnFilters = new WeakMap<Table, Set<ColumnFilter>>();

/**
 * PrimeNG exposes staged menu values through its filter callback and filter icon before Apply is clicked.
 * Menu editors are kept deferred and icons reflect only the last filter state committed by Apply or Clear.
 */
export function patchPrimeNgColumnFilterApplyBehavior(): void {
	if (isPrimeNgColumnFilterPatched) {
		return;
	}

	const originalFilter = Table.prototype._filter;
	const originalClear = Table.prototype.clear;
	const originalOnInit = ColumnFilterFormElement.prototype.onInit;
	const originalColumnFilterOnInit = ColumnFilter.prototype.onInit;
	const originalOnModelChange = ColumnFilterFormElement.prototype.onModelChange;
	const originalOnOverlayBeforeEnter = ColumnFilter.prototype.onOverlayBeforeEnter;
	const originalOnOverlayAnimationAfterLeave = ColumnFilter.prototype.onOverlayAnimationAfterLeave;
	const originalClearFilter = ColumnFilter.prototype.clearFilter;
	const originalApplyFilter = ColumnFilter.prototype.applyFilter;
	const hasFilterDescriptor = Object.getOwnPropertyDescriptor(ColumnFilter.prototype, 'hasFilter');

	Table.prototype._filter = function (): void {
		// Capture committed state right before PrimeNG applies filters, so icon state matches applied results.
		rememberAppliedFilterStates(this);
		originalFilter.call(this);
	};

	Table.prototype.clear = function (): void {
		resetColumnFilterStates(this);
		originalClear.call(this);
		rememberAppliedFilterStates(this);
	};

	ColumnFilterFormElement.prototype.onInit = function (): void {
		originalOnInit.call(this);

		if (isApplyDeferred(this.colFilter)) {
			// callback updates staged values only while popup is open; Apply performs the actual search.
			this.filterCallback = (value: unknown): void => {
				if (this.filterConstraint) {
					this.filterConstraint.value = value;
				}
			};
		}
	};

	ColumnFilter.prototype.onInit = function (): void {
		originalColumnFilterOnInit.call(this);
		registerColumnFilter(this);
	};

	ColumnFilterFormElement.prototype.onModelChange = function (value: unknown): void {
		if (isApplyDeferred(this.colFilter)) {
			//  do not trigger filtering/search on input change until Apply/Clear commits.
			if (this.filterConstraint) {
				this.filterConstraint.value = value;
			}

			return;
		}

		originalOnModelChange.call(this, value);
	};

	ColumnFilter.prototype.onOverlayBeforeEnter = function (event): void {
		if (isApplyDeferred(this)) {
			rememberFilterStateIfMissing(this);
			// PrimeNG edits table.filters in place, so retain its state for cancellation on dismissal.
			pendingFilterStates.set(this, cloneFilterState(this));
		}

		originalOnOverlayBeforeEnter.call(this, event);
	};

	ColumnFilter.prototype.onOverlayAnimationAfterLeave = function (event): void {
		// Closing outside the menu is a cancel unless Apply or Clear removed the saved state.
		restorePendingFilterState(this);
		originalOnOverlayAnimationAfterLeave.call(this, event);
	};

	ColumnFilter.prototype.clearFilter = function (): void {
		// Clear is an explicit commit and must not be rolled back after the overlay closes.
		pendingFilterStates.delete(this);
		originalClearFilter.call(this);
	};

	ColumnFilter.prototype.applyFilter = function (): void {
		// Apply commits the staged values, so discard the cancellation snapshot.
		pendingFilterStates.delete(this);
		originalApplyFilter.call(this);
	};

	if (hasFilterDescriptor?.get) {
		Object.defineProperty(ColumnFilter.prototype, 'hasFilter', {
			...hasFilterDescriptor,
			get: function (): boolean {
				const { dataTable, field } = getColumnFilterInternals(this);
				const appliedState = appliedFilterStates.get(dataTable)?.get(field);

				//icon reflects committed state after Apply/Clear, then falls back to PrimeNG behavior.
				return appliedState ?? hasFilterDescriptor.get?.call(this) ?? false;
			},
		});
	}

	isPrimeNgColumnFilterPatched = true;
}

function isApplyDeferred(colFilter: ColumnFilter | undefined): boolean {
	// Keep Apply-button popup behavior consistent for both main lists and sublists (issue #2).
	return colFilter?.display === 'menu' && colFilter?.showApplyButton !== false;
}

function rememberAppliedFilterStates(table: Table): void {
	const states = new Map<string, boolean>();

	for (const field of Object.keys(table.filters)) {
		states.set(field, hasActiveFilter(table, field));
	}

	appliedFilterStates.set(table, states);
}

function registerColumnFilter(colFilter: ColumnFilter): void {
	const { dataTable } = getColumnFilterInternals(colFilter);
	let columnFilters = tableColumnFilters.get(dataTable);

	if (!columnFilters) {
		columnFilters = new Set<ColumnFilter>();
		tableColumnFilters.set(dataTable, columnFilters);
	}

	columnFilters.add(colFilter);
}

function resetColumnFilterStates(table: Table): void {
	const columnFilters = tableColumnFilters.get(table);

	if (!columnFilters) return;

	columnFilters.forEach(columnFilter => {
		resetColumnFilterState(columnFilter);
		pendingFilterStates.delete(columnFilter);
	});
}

function resetColumnFilterState(colFilter: ColumnFilter): void {
	const { dataTable, display, field, matchMode, operator, type } = getColumnFilterInternals(colFilter);
	const defaultMatchMode = getDefaultMatchMode(type, matchMode);

	dataTable.filters[field] =
		display === 'row' ? { value: null, matchMode: defaultMatchMode } : [{ value: null, matchMode: defaultMatchMode, operator: operator ?? FilterOperator.AND }];
}

function getDefaultMatchMode(type = 'text', matchMode?: string): string {
	if (matchMode) return matchMode;
	if (type === 'text') return FilterMatchMode.STARTS_WITH;
	if (type === 'numeric') return FilterMatchMode.EQUALS;
	if (type === 'date') return FilterMatchMode.DATE_IS;

	return FilterMatchMode.CONTAINS;
}

function rememberFilterStateIfMissing(colFilter: ColumnFilter): void {
	const { dataTable, field } = getColumnFilterInternals(colFilter);
	let states = appliedFilterStates.get(dataTable);

	if (!states) {
		states = new Map<string, boolean>();
		appliedFilterStates.set(dataTable, states);
	}

	if (!states.has(field)) {
		states.set(field, hasActiveFilter(dataTable, field));
	}
}

function cloneFilterState(colFilter: ColumnFilter): FilterMetadata | FilterMetadata[] {
	const { dataTable, field } = getColumnFilterInternals(colFilter);
	const fieldFilter = dataTable.filters[field];

	return Array.isArray(fieldFilter) ? fieldFilter.map(constraint => ({ ...constraint })) : { ...fieldFilter };
}

function restorePendingFilterState(colFilter: ColumnFilter): void {
	const filterState = pendingFilterStates.get(colFilter);

	if (filterState) {
		const { dataTable, field } = getColumnFilterInternals(colFilter);
		// Clone again to avoid a future menu edit mutating the saved snapshot by reference.
		dataTable.filters[field] = Array.isArray(filterState) ? filterState.map(constraint => ({ ...constraint })) : { ...filterState };
		pendingFilterStates.delete(colFilter);
	}
}

function hasActiveFilter(table: Table, field: string): boolean {
	const fieldFilter = table.filters[field];
	const firstConstraint = Array.isArray(fieldFilter) ? fieldFilter[0] : fieldFilter;

	return !!firstConstraint && !table.isFilterBlank(firstConstraint.value);
}

function getColumnFilterInternals(colFilter: ColumnFilter): {
	dataTable: Table;
	display?: string;
	field: string;
	matchMode?: string;
	operator?: string;
	type?: string;
} {
	return colFilter as unknown as {
		dataTable: Table;
		display?: string;
		field: string;
		matchMode?: string;
		operator?: string;
		type?: string;
	};
}
